import { Scene, Vector3 } from '@babylonjs/core';
import { World } from '../core/World';
import { PlayerModel } from './PlayerModel';
import { PlayerPhysics } from './PlayerPhysics';
import { PlayerController, CameraMode } from './PlayerController';
import { FirstPersonCamera } from './FirstPersonCamera';
import { ThirdPersonCamera } from './ThirdPersonCamera';

export interface PlayerConfig {
  spawnPosition?: Vector3;
}

export class Player {
  private world: World | null;

  private model: PlayerModel;
  private physics: PlayerPhysics;
  private controller: PlayerController;
  private firstPersonCamera: FirstPersonCamera;
  private thirdPersonCamera: ThirdPersonCamera;

  constructor(scene: Scene, world: World | null, canvas: HTMLCanvasElement, config?: PlayerConfig) {
    this.world = world;

    // Create player components
    this.model = new PlayerModel(scene);
    this.physics = new PlayerPhysics(world);
    this.firstPersonCamera = new FirstPersonCamera(scene, canvas);
    this.thirdPersonCamera = new ThirdPersonCamera(scene, canvas);

    this.controller = new PlayerController(
      this.physics,
      this.firstPersonCamera,
      this.thirdPersonCamera
    );

    // Set spawn position
    if (config?.spawnPosition) {
      this.setPosition(config.spawnPosition);
    }
  }

  public update(deltaTime: number): void {
    // Update controller (handles input, physics, cameras)
    this.controller.update(deltaTime);

    // Update model position and rotation
    const position = this.physics.getFeetPosition();
    this.model.setPosition(position);
    this.model.setRotation(this.controller.getYaw());

    // Update model animation
    this.updateAnimation();

    // Update model visibility based on camera mode
    this.model.setVisible(this.controller.getCameraMode() === 'third-person');

    // Update world with player position (for chunk loading) - only if world exists
    if (this.world) {
      this.world.updatePlayerPosition(position);
    }
  }

  private updateAnimation(): void {
    if (this.controller.isPlayerMoving()) {
      if (this.controller.isPlayerRunning()) {
        this.model.playRun();
      } else {
        this.model.playWalk();
      }
    } else {
      this.model.playIdle();
    }
  }

  public setPosition(position: Vector3): void {
    this.physics.setPosition(position);
    this.model.setPosition(position);
  }

  public getPosition(): Vector3 {
    return this.physics.getFeetPosition();
  }

  public getEyePosition(): Vector3 {
    return this.physics.getEyePosition();
  }

  public getCameraMode(): CameraMode {
    return this.controller.getCameraMode();
  }

  public setCameraMode(mode: CameraMode): void {
    this.controller.setCameraMode(mode);
    this.model.setVisible(mode === 'third-person');
  }

  public getLookDirection(): Vector3 {
    return this.controller.getLookDirection();
  }

  public isGrounded(): boolean {
    return this.physics.isGrounded;
  }

  public isInWater(): boolean {
    return this.physics.isInWater;
  }

  public getModel(): PlayerModel {
    return this.model;
  }

  public getPhysics(): PlayerPhysics {
    return this.physics;
  }

  public dispose(): void {
    this.model.dispose();
    this.firstPersonCamera.dispose();
    this.thirdPersonCamera.dispose();
  }
}
